import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './components/auth/Login';
import Layout from './components/layout/Layout';

// PosApp tourne sur app.datashipset.com — chargé seulement si on y est
const PosApp = lazy(() => import('./components/pos/PosApp'));

// Auth secondaire
const Register = lazy(() => import('./components/auth/Register'));
const ChangePassword = lazy(() => import('./components/auth/ChangePassword'));

// Dashboard : lazy aussi (chargé après le login)
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));

// Modules lourds : lazy
const ContactList = lazy(() => import('./components/contacts/ContactList'));
const ContactForm = lazy(() => import('./components/contacts/ContactForm'));
const ContactDetail = lazy(() => import('./components/contacts/ContactDetail'));
const ImportContacts = lazy(() => import('./components/contacts/ImportContacts'));
const DuplicateManager = lazy(() => import('./components/contacts/DuplicateManager'));
const Pipeline = lazy(() => import('./components/pipeline/Pipeline'));
const KpiPage = lazy(() => import('./components/kpi/KpiPage'));
const CommercialDashboard = lazy(() => import('./components/commercial/CommercialDashboard'));
const ContentDashboard = lazy(() => import('./components/content/ContentDashboard'));
const TbgDashboard = lazy(() => import('./components/tbg/TbgDashboard'));
const FormationDashboard = lazy(() => import('./components/formation/FormationDashboard'));
const MonthlyReport = lazy(() => import('./components/reports/MonthlyReport'));
const RHDashboard = lazy(() => import('./components/rh/RHDashboard'));
const StockDashboard = lazy(() => import('./components/stock/StockDashboard'));
const StockDestock = lazy(() => import('./components/stock/StockDestock'));
const ProjectsDashboard = lazy(() => import('./components/projects/ProjectsDashboard'));
const DocumentsPage = lazy(() => import('./components/documents/DocumentsPage'));
const FinanceDashboard = lazy(() => import('./components/finance/FinanceDashboard'));
const AuditLog = lazy(() => import('./components/audit/AuditLog'));
const EmailSenders = lazy(() => import('./components/admin/EmailSenders'));
const DocumentSettings = lazy(() => import('./components/admin/DocumentSettings'));
const DepotsVente = lazy(() => import('./components/depotsvente/DepotsVente'));
const SavList = lazy(() => import('./components/sav/SavList'));
const SavScanMenu = lazy(() => import('./components/sav/SavScanMenu'));
const SavDashboard = lazy(() => import('./components/sav/SavDashboard'));
const SavTimelineView = lazy(() => import('./components/sav/SavTimelineView'));
const PosStatsVendeurs = lazy(() => import('./components/pos/PosStatsVendeurs'));
const PosAdminCaisse = lazy(() => import('./components/pos/PosAdminCaisse'));
const AccessAdmin = lazy(() => import('./components/admin/AccessAdmin'));
const Management = lazy(() => import('./components/management/Management'));
const Relances = lazy(() => import('./components/relances/Relances'));
const DiscussionsPage = lazy(() => import('./components/discussions/DiscussionsPage'));
const RechercheDevisFacture = lazy(() => import('./components/devis/RechercheDevisFacture'));

function DefaultRedirect() {
  return <Dashboard />;
}

function RouteFallback() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Détection de sous-domaine caisse : app.datashipset.com → PosApp
// En local, on peut forcer avec ?pos=1 dans l'URL pour tester.
export function isPosHost() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  if (host.startsWith('app.')) return true;
  const params = new URLSearchParams(window.location.search);
  return params.get('pos') === '1';
}

export default function App() {
  // Retire le splash HTML dès que React a vraiment monté (pas avant)
  useEffect(() => {
    const splash = document.getElementById('initial-splash');
    if (splash) splash.remove();
  }, []);

  // Empêche le scroll trackpad / molette de modifier les <input type="number">.
  // Sur trackpad, le scroll 2 doigts change la valeur d'un champ nombre focusé
  // (handicapant en saisie devis/facture). On retire le focus dès qu'un wheel
  // event arrive — la page scrolle normalement à la place.
  useEffect(() => {
    const onWheel = () => {
      const el = document.activeElement;
      if (el && el.tagName === 'INPUT' && el.type === 'number') el.blur();
    };
    document.addEventListener('wheel', onWheel, { passive: true });
    return () => document.removeEventListener('wheel', onWheel);
  }, []);

  if (isPosHost()) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <PosApp />
      </Suspense>
    );
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/change-password" element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            } />

            <Route path="/" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<DefaultRedirect />} />

              <Route path="contacts" element={<ContactList />} />
              <Route path="contacts/new" element={<ContactForm />} />
              <Route path="contacts/:id" element={<ContactDetail />} />
              <Route path="contacts/:id/edit" element={<ContactForm />} />
              <Route path="contacts/import" element={<ImportContacts />} />
              <Route path="contacts/duplicates" element={<DuplicateManager />} />

              <Route path="pipeline" element={<Pipeline />} />
              <Route path="kpi" element={<KpiPage />} />
              <Route path="commercial" element={<CommercialDashboard />} />
              <Route path="content" element={<ContentDashboard />} />
              <Route path="tbg" element={<TbgDashboard />} />
              <Route path="formation" element={<FormationDashboard />} />
              <Route path="rh" element={<RHDashboard />} />
              <Route path="stock" element={<StockDashboard />} />
              <Route path="stock/destockage" element={<StockDestock />} />
              <Route path="projects" element={<ProjectsDashboard />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="finance" element={<FinanceDashboard />} />
              <Route path="depots-vente" element={<DepotsVente />} />
              <Route path="rechercher-devis" element={<RechercheDevisFacture />} />
              <Route path="devis/:id" element={<RechercheDevisFacture type="devis" />} />
              <Route path="facture/:id" element={<RechercheDevisFacture type="facture" />} />
              <Route path="sav" element={<SavList />} />
              <Route path="sav/dashboard" element={<SavDashboard />} />
              <Route path="sav/scan/:ref" element={<SavScanMenu />} />
              <Route path="sav/:id/timeline" element={<SavTimelineView />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="admin/email-senders" element={<EmailSenders />} />
              <Route path="admin/documents" element={<DocumentSettings />} />
              <Route path="reports" element={
                <ProtectedRoute roles={['admin']}>
                  <MonthlyReport />
                </ProtectedRoute>
              } />
              <Route path="stats-vendeurs" element={
                <ProtectedRoute roles={['admin', 'manager']}>
                  <PosStatsVendeurs />
                </ProtectedRoute>
              } />
              <Route path="admin-caisse" element={
                <ProtectedRoute roles={['admin', 'manager']}>
                  <PosAdminCaisse />
                </ProtectedRoute>
              } />
              <Route path="admin/access" element={
                <ProtectedRoute roles={['admin']}>
                  <AccessAdmin />
                </ProtectedRoute>
              } />
              <Route path="management" element={<Management />} />
              <Route path="relances" element={<Relances />} />
              <Route path="discussions" element={<DiscussionsPage />} />
              <Route path="discussions/:id" element={<DiscussionsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
